import {ReactNode} from 'react'
import Card from '../Card'
import {createUseStyles} from 'react-jss'
import {Colors} from '../../constants/colors'
import {IconDefinition, faUpRightFromSquare} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

interface Props {
  title: string
  icon: IconDefinition
  /** Set for cards that open a report, which marks the card as clickable. */
  onClick?: () => void
  flagged?: boolean
  /** Data source(s) the card's content was retrieved from, rendered as quiet text at the bottom right. */
  sources?: string[]
  children?: ReactNode
}

/**
 * Shared shell for the sub cards of a company report. The card is a two column grid
 * where the icon sits in its own gutter, so the title and the card content share the
 * same left text edge across every card regardless of how wide the icon is.
 */
const CompanyReportInfoCard = ({title, icon, onClick, flagged, sources, children}: Props) => {
  const styles = useStyles()

  return (
    <Card
      className={`${styles.card} card ${onClick ? styles.clickable : ''} ${flagged ? styles.warning : ''}`}
      onClick={onClick}>
      <FontAwesomeIcon className={styles.icon} icon={icon} fixedWidth={true}/>
      <div className={styles.header}>
        <h6 className={styles.title} style={{margin: 0}}>{title}</h6>
        {onClick && <FontAwesomeIcon className={styles.openIcon} icon={faUpRightFromSquare}/>}
      </div>
      <div className={styles.body}>
        {children}
      </div>
      {sources && sources.length > 0 && (
        <div className={styles.sources}>
          {sources.length > 1 ? 'Sources: ' : 'Source: '}{sources.join(', ')}
        </div>
      )}
    </Card>
  )
}
export default CompanyReportInfoCard

const useStyles = createUseStyles({
  card: {
    height: '100%',
    backgroundColor: '#4B4B4B',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    // The body row takes the remaining height, so content can be pushed to the
    // bottom of the card with `marginTop: 'auto'`. The third row holds the
    // source line, collapsing to zero height when it isn't rendered.
    gridTemplateRows: 'auto 1fr auto',
    columnGap: 8,
    rowGap: 12,
    borderRadius: 8,
    padding: 14,
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    }
  },
  clickable: {
    cursor: 'pointer'
  },
  warning: {
    boxShadow: '0 0 5px 0 #FF3333',
    '&:hover': {
      boxShadow: '0 0 30px 0 #FF3333',
    }
  },
  icon: {
    gridColumn: 1,
    gridRow: 1,
    alignSelf: 'center',
    color: Colors.euro_600
  },
  header: {
    gridColumn: 2,
    gridRow: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  title: {
    flexGrow: 1
  },
  openIcon: {
    fontSize: 11,
    color: Colors.darkGray3
  },
  body: {
    gridColumn: 2,
    gridRow: 2,
    display: 'flex',
    flexDirection: 'column'
  },
  sources: {
    gridColumn: 2,
    gridRow: 3,
    textAlign: 'right',
    fontSize: 11,
    color: Colors.darkGray3
  }
})
